import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { OnboardingService } from '@/shared/api/services';

// Must mirror UpdateBudgetPage onboarding offset and steps
const INCOME_ONBOARDING_STEP_OFFSET = 100; // income onboarding base: step 0 => 100
const TOTAL_INCOME_ONBOARDING_STEPS = 5;  // 5 steps (0..4)

const RequireBudgetOnboarding: React.FC = () => {
  const location = useLocation();
  const [decision, setDecision] = useState<'loading' | 'allow' | 'redirect'>('loading');

  useEffect(() => {
    let cancelled = false;

    const check = async (): Promise<void> => {
      try {
        // Allow the update budget page itself (it shows the dialog)
        if (location.pathname === '/update-budget') {
          if (!cancelled) setDecision('allow');
          return;
        }

        // Resolve customerId
        let customerId: number | null = null;
        try {
          const raw = typeof window !== 'undefined' ? localStorage.getItem('customerData') : null;
          const obj = raw ? JSON.parse(raw) : null;
          const maybeId = obj ? (obj.customerId ?? obj.id) : null;
          customerId = typeof maybeId === 'number' ? maybeId : null;
        } catch {
          customerId = null;
        }

        if (!customerId) {
          try {
            const rawUser = typeof window !== 'undefined' ? localStorage.getItem('userData') : null;
            const userObj = rawUser ? JSON.parse(rawUser) : null;
            const userId = userObj?.userId;
            if (typeof userId === 'number') {
              const resp = await OnboardingService.getCustomerByUserId(userId, { skipErrorCapture: true });
              const data = resp?.data as Record<string, unknown> | undefined;
              const maybeId = data ? (data['customerId'] ?? data['id']) : undefined;
              const parsedId = typeof maybeId === 'number' ? maybeId : null;
              if (parsedId && parsedId > 0) {
                customerId = parsedId;
                try { localStorage.setItem('customerData', JSON.stringify(data)); } catch { /* ignore */ }
              }
            }
          } catch {
            // ignore
          }
        }

        // If we still can't resolve a customer, redirect to start onboarding
        if (!customerId) {
          if (!cancelled) setDecision('redirect');
          return;
        }

        // Fetch last saved field stage and decide
        const stageResp = await OnboardingService.getCustomerFieldStage(Number(customerId), { skipErrorCapture: true });
        const stageStr = stageResp?.fieldStage;

        // Completed when persisted stage >= base + total steps (sentinel, e.g. 105 for 5 steps)
        let isCompleted = false;
        if (typeof stageStr === 'string') {
          const parsed = parseInt(stageStr, 10);
          if (!Number.isNaN(parsed)) {
            isCompleted = parsed >= (INCOME_ONBOARDING_STEP_OFFSET + TOTAL_INCOME_ONBOARDING_STEPS);
          }
        }

        if (!cancelled) setDecision(isCompleted ? 'allow' : 'redirect');
      } catch {
        if (!cancelled) setDecision('redirect');
      }
    };

    void check();
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (decision === 'loading') return null;
  if (decision === 'redirect') {
    return <Navigate to="/update-budget?onboardingPending=true" replace state={{ from: location }} />;
  }
  return <Outlet />;
};

export default RequireBudgetOnboarding;


